/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { PureComponent } from 'react';
import memoizeLast from 'transmute/memoizeLast';
import getIn from 'transmute/getIn';
import { Map as ImmutableMap, OrderedMap } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import emptyFunction from 'react-utils/emptyFunction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { UNINITIALIZED, LOADING, SUCCEEDED, FAILED } from 'calling-lifecycle-internal/async-flux-utils/constants/apiLoadingStates';
import CalleeOptions from './CalleeOptions';
import { getObjectId, getObjectTypeId, getFromObject, getCallableObjects, getHasMoreCallees, getAssociatedObjects, getResults } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { AssociatedObjectType } from 'calling-lifecycle-internal/callees/records/CalleesRecordsTyped';
import { isCallableObjectTypeId } from 'calling-lifecycle-internal/callees/operators/isCallableObjectTypeId';
import { fetchCalleeSearchResults } from '../clients/CalleeSearchClient';
import CalleesSkeleton from './CalleesSkeleton';
import CalleeSearchInput from './CalleeSearchInput';
import CalleeSelectMessage from './CalleeSelectMessage';
import { COMPANY_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { CallableObject, PhoneNumberPropertyMetadata
// @ts-expect-error module not typed
} from 'calling-lifecycle-internal/callees/records/CalleesRecordsTyped';
import { HELP_DESK, INBOX } from 'calling-internal-common/constants/appIdentifiers';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class SearchableCalleeList extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      searchText: '',
      searchStatus: UNINITIALIZED,
      searchResults: OrderedMap()
    };
    this.getCallableObjects = memoizeLast((calleesData, searchResults, searchStatus) => {
      if (searchStatus === SUCCEEDED) {
        return searchResults;
      }
      return getCallableObjects(calleesData);
    });
    this.getHasMoreCallees = memoizeLast(calleesData => {
      return getHasMoreCallees(calleesData);
    });
    this.getFromObjectInfo = memoizeLast(calleesData => {
      const fromObject = getFromObject(calleesData);
      const objectTypeId = getObjectTypeId(fromObject);
      const objectId = getObjectId(fromObject);
      return {
        objectTypeId,
        objectId
      };
    });
    this.createSearchResultsList = data => {
      const associatedObjectPage = getIn(['callableObjectAndAssociations', 'associatedObjectsPage'], data) || [];
      return associatedObjectPage.reduce((orderedMap, page) => {
        const associatedObjectType = AssociatedObjectType.fromJS(page);
        const associatedObjects = getAssociatedObjects(associatedObjectType);
        const associatedObjectsResults = getResults(associatedObjects);
        return orderedMap.merge(associatedObjectsResults);
      }, OrderedMap());
    };
    this.handleSetSearchText = searchText => {
      this.setState({
        searchText
      });
    };
    this.handleClearSearch = () => {
      const {
        onUpdateSearchResults
      } = this.props;
      const searchResults = OrderedMap();
      this.setState({
        searchText: '',
        searchStatus: UNINITIALIZED,
        searchResults
      }, () => {
        onUpdateSearchResults({
          searchResults
        });
      });
    };
    this.handleRetrySearch = () => {
      this.handleSearch({
        query: this.state.searchText,
        timestamp: Date.now()
      });
    };
    this.handleSearch = ({
      query: searchText,
      timestamp
    }) => {
      const {
        calleesData,
        onUpdateSearchResults
      } = this.props;
      const {
        objectId,
        objectTypeId
      } = this.getFromObjectInfo(calleesData);
      this.setState({
        searchText,
        searchStatus: LOADING
      });
      fetchCalleeSearchResults({
        timestamp,
        searchText,
        objectId,
        objectTypeId
      }).then(result => {
        if (result.timestamp === timestamp) {
          if (result.response) {
            const searchResults = this.createSearchResultsList(result.response);
            this.setState({
              searchStatus: SUCCEEDED,
              searchResults: this.createSearchResultsList(result.response)
            }, () => onUpdateSearchResults({
              searchResults
            }));
          } else {
            const searchResults = OrderedMap();
            this.setState({
              searchStatus: FAILED,
              searchResults
            }, () => onUpdateSearchResults({
              searchResults
            }));
          }
        }
      });
    };
  }
  renderSearchPrompt() {
    const hasMoreCallees = this.getHasMoreCallees(this.props.calleesData);
    const {
      objectTypeId
    } = this.getFromObjectInfo(this.props.calleesData);
    const isCompanyObjectType = objectTypeId === COMPANY_TYPE_ID;
    if (!hasMoreCallees || this.state.searchText.length > 0 || !isCompanyObjectType) return null;
    return /*#__PURE__*/_jsx(CalleeSelectMessage, {
      isHelp: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "calling-ui-library.callee-select.searchForContacts"
      })
    });
  }
  renderCalleeOptions() {
    const {
      appIdentifier,
      calleesData,
      onSelectToNumber,
      disabled,
      onAddPropertyClick,
      CallSettingsIcon,
      onEditProperty,
      editPermissionsResults,
      showSingleContactNames,
      disabledTooltipMessage,
      showAddPropertyButton,
      isKnownVisitor,
      visitorPhoneNumber
    } = this.props;
    const {
      searchStatus,
      searchResults,
      searchText
    } = this.state;
    const isHelpdesk = appIdentifier === HELP_DESK;
    const isInbox = appIdentifier === INBOX;
    let callableObjectList;
    if (!isKnownVisitor && (isHelpdesk || isInbox)) {
      const callableUnknownContact = CallableObject.fromJS({
        name: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "callee-selection.unknownContact"
        }),
        phoneNumberProperties: OrderedMap({
          phone: {
            propertyName: 'phone',
            label: 'Phone Number',
            hubspotDefined: true,
            value: visitorPhoneNumber,
            metadata: new PhoneNumberPropertyMetadata({
              validNumber: true,
              phoneNumber: visitorPhoneNumber
            })
          }
        }),
        crmObjectSubscriptionStatus: ImmutableMap({
          optedOutOfCommunications: false
        }),
        canCommunicate: true
      });
      callableObjectList = new OrderedMap({
        'unknown contact': callableUnknownContact
      });
    } else {
      callableObjectList = calleesData && this.getCallableObjects(calleesData, searchResults, searchStatus);
    }
    const {
      objectTypeId
    } = this.getFromObjectInfo(calleesData);
    const isSingleContact = isCallableObjectTypeId(objectTypeId) && callableObjectList.size === 1;
    const showCalleeNames = showSingleContactNames || !isSingleContact && !showSingleContactNames;
    if (searchStatus === LOADING) {
      return /*#__PURE__*/_jsx(CalleesSkeleton, {
        showCalleeNames: showCalleeNames
      });
    } else if (searchStatus === FAILED) {
      return /*#__PURE__*/_jsx(CalleeSelectMessage, {
        isError: true,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "calling-ui-library.callee-select.searchFailed_jsx",
          elements: {
            UILink: Link
          },
          options: {
            onClick: this.handleRetrySearch
          }
        })
      });
    } else if (searchText.length > 0 && searchText.length < 3) {
      return /*#__PURE__*/_jsx(CalleeSelectMessage, {
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "calling-ui-library.callee-select.minimumSearchCharacters"
        })
      });
    } else if (callableObjectList && callableObjectList.size === 0) {
      if (searchStatus === SUCCEEDED) {
        return /*#__PURE__*/_jsx(CalleeSelectMessage, {
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "calling-ui-library.callee-select.noSearchResults"
          })
        });
      }
      return null;
    }
    return /*#__PURE__*/_jsx(CalleeOptions, {
      appIdentifier: appIdentifier,
      showCalleeNames: showCalleeNames,
      callableObjectList: callableObjectList,
      onSelectToNumber: onSelectToNumber,
      onEditProperty: onEditProperty,
      disabled: disabled,
      onAddPropertyClick: onAddPropertyClick,
      OptionIcon: CallSettingsIcon,
      canEdit: true,
      showAddPropertyButton: showAddPropertyButton,
      editPermissionsResults: editPermissionsResults,
      disabledTooltipMessage: disabledTooltipMessage
    });
  }
  render() {
    const {
      calleesData
    } = this.props;
    const {
      searchText
    } = this.state;
    const hasMoreCallees = this.getHasMoreCallees(calleesData);
    const {
      objectTypeId
    } = this.getFromObjectInfo(calleesData);
    // TODO: fix search to work with other object types not just company
    const isCompanyObjectType = objectTypeId === COMPANY_TYPE_ID;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [hasMoreCallees && isCompanyObjectType ? /*#__PURE__*/_jsx(CalleeSearchInput, {
        onClear: this.handleClearSearch,
        onSearch: this.handleSearch,
        setSearchText: this.handleSetSearchText,
        searchText: searchText
      }) : null, this.renderCalleeOptions(), this.renderSearchPrompt()]
    });
  }
}
SearchableCalleeList.propTypes = {
  appIdentifier: PropTypes.string,
  calleesData: RecordPropType('CalleesRecord'),
  editPermissionsResults: ImmutablePropTypes.map,
  onSelectToNumber: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onAddPropertyClick: PropTypes.func.isRequired,
  onEditProperty: PropTypes.func,
  onUpdateSearchResults: PropTypes.func,
  CallSettingsIcon: PropTypes.object,
  showSingleContactNames: PropTypes.bool,
  disabledTooltipMessage: PropTypes.string,
  showAddPropertyButton: PropTypes.bool,
  isKnownVisitor: PropTypes.bool,
  visitorPhoneNumber: PropTypes.string
};
SearchableCalleeList.defaultProps = {
  onUpdateSearchResults: emptyFunction,
  onAddPropertyClick: emptyFunction,
  CallSettingsIcon: null,
  disabled: false,
  showAddPropertyButton: true,
  showSingleContactNames: true
};